// components/CheckoutModal.jsx
import React, { useMemo, useState, useEffect } from "react";
import allImages from "../../../assets/images-import.jsx";
import ProductSearchSelect from "./ProductSearchSelect.jsx";

const sampleProducts = [
  {
    id: "p1",
    label: "Almonds Premium",
    price: 250.0,
    img: allImages.prodA || allImages.logoSm,
    weight: "500 g",
  },
  {
    id: "p2",
    label: "Cashew Roasted",
    price: 350.0,
    img: allImages.prodB || allImages.logoSm,
    weight: "250 g",
  },
  {
    id: "p3",
    label: "Mixed Dry Fruit",
    price: 450.0,
    img: allImages.prodC || allImages.logoSm,
    weight: "1 kg",
  },
];

const paymentMethods = ["GPay", "PhonePe", "Paytm", "NetBanking", "Card"];

export default function CheckoutModal({
  isOpen,
  onClose,
  initialUser = {},
  onPayment = (payload) => console.log("Pay", payload),
}) {
  const defaultForm = {
    userName: initialUser.userName || "",
    mobileNo: initialUser.mobileNo || "",
    emailId: initialUser.emailId || "",
    companyName: "",
    gstNo: "",
    country: "",
    buildingName: "",
    street: "",
    pinCode: "",
    city: "",
    state: "",
  };

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm);

  const [productRows, setProductRows] = useState([
    { rowId: Date.now(), productId: sampleProducts[0].id, qty: 1 },
  ]);

  const [paymentPlan, setPaymentPlan] = useState("full");
  const [partAmount, setPartAmount] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);

  // NEW: special discount state (amount in ₹)
  const [specialDiscount, setSpecialDiscount] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setForm(defaultForm);
      setProductRows([
        { rowId: Date.now(), productId: sampleProducts[0].id, qty: 1 },
      ]);
      setPaymentPlan("full");
      setPartAmount("");
      setSelectedMethod(paymentMethods[0]);
      setSpecialDiscount("");
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    // keep form in sync if initialUser changes while open
    setForm((f) => ({
      ...defaultForm,
      companyName: f.companyName,
      gstNo: f.gstNo,
      userName: f.userName || f.userName,
      mobileNo: f.mobileNo || f.mobileNo,
    }));
    // eslint-disable-next-line
  }, [initialUser]);

  const updateFormField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const addProductRow = () =>
    setProductRows((r) => [
      ...r,
      {
        rowId: Date.now() + Math.random(),
        productId: sampleProducts[0].id,
        qty: 1,
      },
    ]);
  const removeProductRow = (rowId) =>
    setProductRows((r) =>
      r.length === 1 ? r : r.filter((x) => x.rowId !== rowId)
    );
  const changeRow = (rowId, key, value) =>
    setProductRows((rows) =>
      rows.map((r) => (r.rowId === rowId ? { ...r, [key]: value } : r))
    );

  const incQty = (rowId) => {
    const cur = Number(productRows.find((r) => r.rowId === rowId)?.qty || 1);
    changeRow(rowId, "qty", cur + 1);
  };
  const decQty = (rowId) => {
    const cur = Number(productRows.find((r) => r.rowId === rowId)?.qty || 1);
    changeRow(rowId, "qty", Math.max(1, cur - 1));
  };

  const calcRowAmount = (row) => {
    const p = sampleProducts.find((s) => s.id === row.productId);
    if (!p) return 0;
    return p.price * (Number(row.qty) || 0);
  };

  const subtotal = useMemo(
    () => productRows.reduce((a, r) => a + calcRowAmount(r), 0),
    [productRows]
  );
  const deliveryCharge = subtotal > 0 ? 50 : 0;

  // compute discount number, clamp to available amount
  const parsedDiscount = (() => {
    const n = Number(specialDiscount);
    if (!specialDiscount) return 0;
    if (Number.isNaN(n) || n <= 0) return 0;
    const maxAllowed = subtotal + deliveryCharge;
    return Math.min(n, maxAllowed);
  })();

  const discount = parsedDiscount;
  const grandTotal = Number(
    Math.max(0, subtotal + deliveryCharge - discount).toFixed(2)
  );

  // payableNow must consider discounted grandTotal
  const payableNow =
    paymentPlan === "full"
      ? grandTotal
      : Math.min(grandTotal, Number(partAmount) || 0);

  const validateStep1 = () => {
    if (!form.userName?.trim()) return { ok: false, msg: "Enter user name" };
    if (!form.mobileNo?.trim())
      return { ok: false, msg: "Enter mobile number" };
    if (!productRows.some((r) => r.productId))
      return { ok: false, msg: "Select at least one product" };
    if (
      paymentPlan === "part" &&
      (partAmount === "" || Number(partAmount) <= 0)
    )
      return { ok: false, msg: "Enter amount to pay now for part payment" };
    if (paymentPlan === "part" && Number(partAmount) > grandTotal)
      return {
        ok: false,
        msg: "Amount to pay now cannot exceed grand total after discount",
      };
    return { ok: true };
  };

  const handleGenerateAndReview = () => {
    const v = validateStep1();
    if (!v.ok) return alert(v.msg);
    setStep(2);
  };

  const handleMakePayment = () => {
    const payload = {
      user: form,
      products: productRows.map((r) => {
        const det = sampleProducts.find((p) => p.id === r.productId) || {};
        return {
          ...r,
          label: det.label,
          price: det.price,
          img: det.img,
          weight: det.weight,
        };
      }),
      totals: { subtotal, deliveryCharge, discount, grandTotal, payableNow },
      payment: {
        plan: paymentPlan,
        partAmount: paymentPlan === "part" ? Number(partAmount) : null,
        method: selectedMethod,
      },
    };
    onPayment(payload);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      role="dialog"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered"
        role="document"
        style={{ maxWidth: 1000 }}
      >
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title">
              {step === 1 ? "Checkout — Details" : "Checkout — Review"}
            </h5>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {step === 1 && (
              <div className="row">
                <div className="col-lg-7">
                  {/* Customer & Address */}
                  <div className="card mb-3 p-3" style={{ borderRadius: 8 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Customer & Address</h6>
                      <small className="text-muted">
                        Contact & shipping info
                      </small>
                    </div>

                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label small">Name</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.userName}
                          onChange={(e) =>
                            updateFormField("userName", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Mobile</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.mobileNo}
                          onChange={(e) =>
                            updateFormField("mobileNo", e.target.value)
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small">Email</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.emailId}
                          onChange={(e) =>
                            updateFormField("emailId", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Company</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.companyName}
                          onChange={(e) =>
                            updateFormField("companyName", e.target.value)
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small">GST No</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.gstNo}
                          onChange={(e) =>
                            updateFormField("gstNo", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Country</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.country}
                          onChange={(e) =>
                            updateFormField("country", e.target.value)
                          }
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label small">
                          Building / Name
                        </label>
                        <input
                          className="form-control form-control-sm"
                          value={form.buildingName}
                          onChange={(e) =>
                            updateFormField("buildingName", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">
                          Street / Locality
                        </label>
                        <input
                          className="form-control form-control-sm"
                          value={form.street}
                          onChange={(e) =>
                            updateFormField("street", e.target.value)
                          }
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="form-label small">City</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.city}
                          onChange={(e) =>
                            updateFormField("city", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">Pin Code</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.pinCode}
                          onChange={(e) =>
                            updateFormField("pinCode", e.target.value)
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">State</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.state}
                          onChange={(e) =>
                            updateFormField("state", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="card mb-3 p-3" style={{ borderRadius: 8 }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Products</h6>
                      <small className="text-muted">
                        Select products & quantity
                      </small>
                    </div>
                    {productRows.map((row) => {
                      const det =
                        sampleProducts.find((p) => p.id === row.productId) ||
                        {};
                      return (
                        <div
                          key={row.rowId}
                          className="d-flex align-items-center justify-content-between py-2 border-bottom"
                          style={{ gap: 12 }}
                        >
                          {/* Left: image + product select (inline, centered) */}
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: 12, minWidth: 0, flex: 1 }}
                          >
                            {/* Product select fills remaining space (keeps inline) */}
                            <div style={{ flex: 1, minWidth: 160 }}>
                              <ProductSearchSelect
                                products={sampleProducts}
                                value={row.productId}
                                onChange={(pid) =>
                                  changeRow(row.rowId, "productId", pid)
                                }
                                placeholder="Search product..."
                              />
                            </div>
                          </div>

                          {/* Right: quantity, line total, delete — inline and centered */}
                          <div
                            className="d-flex align-items-center"
                            style={{ gap: 12, marginLeft: 12 }}
                          >
                            <div
                              className="input-group input-group-sm"
                              style={{ width: 110 }}
                            >
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => decQty(row.rowId)}
                              >
                                -
                              </button>
                              <input
                                className="form-control text-center"
                                value={row.qty}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => incQty(row.rowId)}
                              >
                                +
                              </button>
                            </div>

                            <div style={{ minWidth: 90, textAlign: "right" }}>
                              <div
                                className="small text-muted"
                                style={{ lineHeight: 1 }}
                              >
                                Line
                              </div>
                              <div className="fw-medium">
                                ₹{calcRowAmount(row).toFixed(2)}
                              </div>
                            </div>

                            <button
                              className="btn btn-sm btn-outline-danger"
                              disabled={productRows.length === 1}
                              onClick={() => removeProductRow(row.rowId)}
                            >
                              Del
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div className="mt-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={addProductRow}
                      >
                        + Add product
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-lg-5">
                  {/* NEW: Special Discount field above Payment Options */}
                  <div className="card p-3 mb-3" style={{ borderRadius: 8 }}>
                    <h6 className="mb-2">Special Discount</h6>
                    <div className="small text-muted mb-2">
                      Enter an amount to discount from the order total (₹)
                    </div>
                    <div className="d-flex gap-2">
                      <input
                        type="number"
                        min="0"
                        className="form-control form-control-sm"
                        placeholder="0.00"
                        value={specialDiscount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "") return setSpecialDiscount("");
                          const n = Number(v);
                          if (Number.isNaN(n)) return;
                          // clamp to subtotal + deliveryCharge
                          const maxAllowed = subtotal + deliveryCharge;
                          setSpecialDiscount(
                            String(Math.min(Math.max(0, n), maxAllowed))
                          );
                        }}
                      />
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setSpecialDiscount("")}
                      >
                        Clear
                      </button>
                    </div>
                    <div className="small text-muted mt-2">
                      Max discount: ₹{(subtotal + deliveryCharge).toFixed(2)}
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="card p-3 mb-3" style={{ borderRadius: 8 }}>
                    <h6 className="mb-2">Payment Options</h6>

                    <div className="mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="plan"
                          id="fullPlan"
                          checked={paymentPlan === "full"}
                          onChange={() => setPaymentPlan("full")}
                        />
                        <label className="form-check-label">Pay Full</label>
                      </div>
                      <div className="form-check mt-1">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="plan"
                          id="partPlan"
                          checked={paymentPlan === "part"}
                          onChange={() => setPaymentPlan("part")}
                        />
                        <label className="form-check-label">Pay Part</label>
                      </div>
                    </div>

                    {paymentPlan === "part" ? (
                      <>
                        <label className="form-label small">
                          Amount to pay now (₹)
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="form-control form-control-sm mb-2"
                          value={partAmount}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === "") return setPartAmount("");
                            const n = Number(v);
                            if (Number.isNaN(n)) return;
                            setPartAmount(String(Math.min(n, grandTotal)));
                          }}
                        />
                        <div className="small text-muted mb-2">
                          Maximum ₹{grandTotal.toFixed(2)}
                        </div>
                      </>
                    ) : (
                      <div className="small text-muted mb-2">
                        Payable: <strong>₹{grandTotal.toFixed(2)}</strong>
                      </div>
                    )}

                    <label className="form-label small">Payment Method</label>
                    <select
                      className="form-select form-select-sm mb-3"
                      value={selectedMethod}
                      onChange={(e) => setSelectedMethod(e.target.value)}
                    >
                      {paymentMethods.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>

                    <div className="border-top pt-2">
                      <div className="d-flex justify-content-between small">
                        <div>Subtotal</div>
                        <div>₹{subtotal.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <div>Delivery</div>
                        <div>₹{deliveryCharge.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <div>Special Discount</div>
                        <div>- ₹{discount.toFixed(2)}</div>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between fw-bold">
                        <div>Grand Total</div>
                        <div>₹{grandTotal.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small mt-1">
                        <div>Payable now</div>
                        <div>₹{payableNow.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="card p-3" style={{ borderRadius: 8 }}>
                    <h6 className="mb-1">Quick Summary</h6>
                    <div className="small text-muted mb-2">
                      Customer — {form.userName || "—"}
                    </div>
                    <div className="small text-muted mb-2">
                      Contact — {form.mobileNo || "—"}
                    </div>
                    <div className="small text-muted mb-2">
                      GST — {form.gstNo || "—"}
                    </div>

                    <div className="mt-3 d-grid">
                      <button
                        className="btn btn-primary"
                        onClick={handleGenerateAndReview}
                      >
                        Generate & Review
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <>
                <div className="row">
                  <div className="col-lg-7">
                    <div className="card p-3 mb-3">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6>Shipping</h6>
                          <div className="small text-muted">
                            {form.userName} • {form.mobileNo}
                          </div>
                          <div className="small text-muted mt-1">
                            {form.buildingName}
                            {form.street ? `, ${form.street}` : ""}
                            <br />
                            {form.city} - {form.pinCode}, {form.state}
                          </div>
                          {form.gstNo && (
                            <div className="small text-muted mt-1">
                              GST: {form.gstNo}
                            </div>
                          )}
                        </div>
                        <div className="text-end">
                          <small className="text-muted">Payment</small>
                          <div className="fw-medium">
                            {paymentPlan === "full"
                              ? "Full"
                              : `Part — ₹${(Number(partAmount) || 0).toFixed(
                                  2
                                )}`}
                          </div>
                          <div className="small text-muted">
                            {selectedMethod}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card p-3">
                      <h6>Products</h6>
                      {productRows.map((r) => {
                        const det =
                          sampleProducts.find((p) => p.id === r.productId) ||
                          {};
                        return (
                          <div
                            key={r.rowId}
                            className="d-flex align-items-center justify-content-between py-2 border-bottom"
                          >
                            <div className="d-flex gap-3 align-items-center">
                              <img
                                src={det.img}
                                alt={det.label}
                                style={{
                                  width: 64,
                                  height: 64,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                              />
                              <div>
                                <div className="fw-medium">{det.label}</div>
                                <div className="small text-muted">
                                  {det.weight}
                                </div>
                                <div className="small text-muted">
                                  Qty: {r.qty} • ₹{det.price.toFixed(2)} each
                                </div>
                              </div>
                            </div>
                            <div className="fw-medium">
                              ₹{(det.price * r.qty).toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="col-lg-5">
                    <div className="card p-3">
                      <h6>Order Summary</h6>
                      <div className="d-flex justify-content-between small">
                        <div>Subtotal</div>
                        <div>₹{subtotal.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <div>Delivery</div>
                        <div>₹{deliveryCharge.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <div>Special Discount</div>
                        <div>- ₹{discount.toFixed(2)}</div>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between fw-bold">
                        <div>Grand Total</div>
                        <div>₹{grandTotal.toFixed(2)}</div>
                      </div>
                      <div className="d-flex justify-content-between small mt-2">
                        <div>Payable now</div>
                        <div>₹{payableNow.toFixed(2)}</div>
                      </div>

                      <div className="mt-3 d-grid">
                        <button
                          className="btn btn-success"
                          onClick={handleMakePayment}
                        >
                          Generate Payment Link
                        </button>
                        <button
                          className="btn btn-outline-secondary mt-2"
                          onClick={() => setStep(1)}
                        >
                          Back & Edit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
